<script lang="ts">
  import { onMount } from "svelte";
  import { gmailState } from "../gmail/store.svelte";
  import { closeCompose, sendDraft, addAttachments, removeAttachment } from "../gmail/compose";
  import { getSignature } from "../gmail/signature";
  import { searchContacts, type Contact } from "../gmail/contacts.svelte";

  let fileInput: HTMLInputElement | undefined = $state();

  function onPickFiles() {
    fileInput?.click();
  }
  function onFilesSelected(e: Event) {
    const target = e.target as HTMLInputElement;
    const files = target.files ? Array.from(target.files) : [];
    if (files.length > 0) addAttachments(files);
    target.value = ""; // reset pour permettre de re-pick le même file
  }
  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  let sending = $state(false);
  let error = $state<string | null>(null);
  let signatureHtml = $state<string>("");

  // Charge la signature pour l'aperçu (cache instant si dispo)
  onMount(() => {
    getSignature()
      .then((sig) => (signatureHtml = sig))
      .catch((e) => console.warn("getSignature in compose:", e));
  });

  async function onSend() {
    if (sending) return;
    sending = true;
    error = null;
    const r = await sendDraft();
    if (!r.ok) {
      error = r.error ?? "Échec de l'envoi";
      sending = false;
    }
    // Si OK, sendDraft a déjà fermé le compose (gmailState.composeDraft = null)
  }

  function onCancel() {
    if (sending) return;
    closeCompose();
  }

  // Esc → cancel (desktop)
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape" && !sending) {
      e.preventDefault();
      closeCompose();
    }
    // Cmd/Ctrl+Enter → send
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void onSend();
    }
  }

  // Reactive draft (binding direct sur gmailState.composeDraft)
  const d = $derived(gmailState.composeDraft);

  // === Autocomplete contacts (To/Cc/Bcc) ===================================
  // On track quel champ a le focus pour savoir où afficher les suggestions.
  // L'input contient potentiellement plusieurs adresses séparées par des
  // virgules ; on suggère sur le DERNIER token (= ce que l'user est en
  // train de taper après la dernière virgule).
  type RecipientField = "to" | "cc" | "bcc";
  let focusedField = $state<RecipientField | null>(null);
  let toEl: HTMLInputElement | undefined = $state();
  let ccEl: HTMLInputElement | undefined = $state();
  let bccEl: HTMLInputElement | undefined = $state();

  interface SuggestionContext {
    field: RecipientField;
    /** Index dans la valeur du champ où le partial commence (juste après la virgule). */
    tokenStart: number;
    partial: string;
    list: Contact[];
  }

  const suggestions = $derived.by<SuggestionContext | null>(() => {
    if (!focusedField || !d) return null;
    const value = d[focusedField] ?? "";
    const lastComma = value.lastIndexOf(",");
    const partial = value.slice(lastComma + 1).trim();
    if (partial.length < 1) return null;
    const list = searchContacts(partial, 6);
    if (list.length === 0) return null;
    return {
      field: focusedField,
      tokenStart: lastComma + 1,
      partial,
      list,
    };
  });

  function applySuggestion(c: Contact) {
    if (!suggestions || !d) return;
    const field = suggestions.field;
    const before = (d[field] ?? "").slice(0, suggestions.tokenStart);
    // Si before n'est pas vide et ne finit pas par espace, on ajoute un
    // espace de séparation avant l'email pour la lisibilité ("a@x.com,b" → "a@x.com, b").
    const sep = before.length > 0 && !/\s$/.test(before) ? " " : "";
    d[field] = before + sep + c.email + ", ";
    // Re-focus le champ pour permettre de taper l'adresse suivante
    const el = field === "to" ? toEl : field === "cc" ? ccEl : bccEl;
    el?.focus();
  }

  // Délai sur blur pour laisser le temps au tap-suggestion de se déclencher
  // avant que la liste disparaisse. mousedown.preventDefault sur le bouton
  // suggestion empêche aussi de perdre le focus au tap (belt-and-suspenders).
  function onFieldBlur() {
    setTimeout(() => {
      focusedField = null;
    }, 150);
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if d}
  <div class="overlay" role="presentation">
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Composer un message">
      <header>
        <button class="ghost" onclick={onCancel} disabled={sending}>
          Annuler
        </button>
        {#if d.replyToFromLabel}
          <div class="title muted">À {d.replyToFromLabel}</div>
        {:else}
          <div class="title muted">Nouveau message</div>
        {/if}
        <button class="primary" onclick={onSend} disabled={sending}>
          {sending ? "Envoi…" : "Envoyer"}
        </button>
      </header>

      <div class="form">
        {#snippet suggestionsList()}
          {#if suggestions}
            <ul class="suggestions" role="listbox">
              {#each suggestions.list as c (c.email)}
                <li>
                  <button
                    type="button"
                    class="suggestion"
                    onmousedown={(e) => e.preventDefault()}
                    onclick={() => applySuggestion(c)}
                  >
                    <span class="sugg-name">{c.name || c.email}</span>
                    {#if c.name}
                      <span class="sugg-email">{c.email}</span>
                    {/if}
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
        {/snippet}

        {#if !d.replyToFromLabel}
          <!-- Pour un nouveau mail seulement, on montre To/Cc/Bcc -->
          <div class="row">
            <span class="lbl">À</span>
            <input
              bind:this={toEl}
              type="text"
              bind:value={d.to}
              placeholder="email@example.com"
              autocomplete="off"
              autocapitalize="off"
              spellcheck="false"
              disabled={sending}
              onfocus={() => (focusedField = "to")}
              onblur={onFieldBlur}
            />
          </div>
          {#if focusedField === "to"}
            {@render suggestionsList()}
          {/if}
          {#if d.showCcBcc}
            <div class="row">
              <span class="lbl">Cc</span>
              <input
                bind:this={ccEl}
                type="text"
                bind:value={d.cc}
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
                disabled={sending}
                onfocus={() => (focusedField = "cc")}
                onblur={onFieldBlur}
              />
            </div>
            {#if focusedField === "cc"}
              {@render suggestionsList()}
            {/if}
            <div class="row">
              <span class="lbl">Bcc</span>
              <input
                bind:this={bccEl}
                type="text"
                bind:value={d.bcc}
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
                disabled={sending}
                onfocus={() => (focusedField = "bcc")}
                onblur={onFieldBlur}
              />
            </div>
            {#if focusedField === "bcc"}
              {@render suggestionsList()}
            {/if}
          {:else}
            <button class="link" onclick={() => (d.showCcBcc = true)} type="button">
              + Cc / Bcc
            </button>
          {/if}
          <div class="row">
            <span class="lbl">Objet</span>
            <input
              type="text"
              bind:value={d.subject}
              disabled={sending}
            />
          </div>
        {:else}
          <!-- Reply : on cache to/subject (déjà fixés). Affichage compact. -->
          <div class="row compact">
            <span class="lbl">Objet</span>
            <input
              type="text"
              bind:value={d.subject}
              disabled={sending}
            />
          </div>
        {/if}

        <textarea
          bind:value={d.body}
          placeholder={d.replyToFromLabel ? "Écrire votre réponse…" : "Écrire votre message…"}
          disabled={sending}
        ></textarea>

        {#if signatureHtml}
          <div class="signature">
            <div class="sig-label">— Signature —</div>
            <iframe
              srcdoc={'<style>html,body{margin:0;padding:8px;font:13px/1.4 -apple-system,system-ui,sans-serif;color:#57534e;}img{max-width:100%;height:auto;}@media(prefers-color-scheme:dark){html,body{color:var(--fg-subtle);background:transparent;}}</style>' + signatureHtml}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              title="Signature preview"
              style="width:100%;border:none;background:transparent;min-height:80px;"
            ></iframe>
          </div>
        {/if}
      </div>

      {#if error}
        <div class="error">⚠ {error}</div>
      {/if}

      <!-- Attachments : chips + total + bouton ajouter -->
      {#if d.attachments.length > 0}
        <div class="attachments-list">
          {#each d.attachments as a (a.uid)}
            <div class="att-chip">
              <span class="att-name">{a.filename}</span>
              <span class="att-size">{formatSize(a.size)}</span>
              <button
                class="att-remove"
                onclick={() => removeAttachment(a.uid)}
                aria-label="Retirer {a.filename}"
                disabled={sending}
              >×</button>
            </div>
          {/each}
        </div>
      {/if}

      <!-- Footer : pj + track + total -->
      <div class="footer-toggle">
        <button class="icon-link" onclick={onPickFiles} disabled={sending} aria-label="Joindre">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <input
          type="file"
          bind:this={fileInput}
          onchange={onFilesSelected}
          multiple
          style="display:none"
        />

        <label class="track-toggle">
          <input type="checkbox" bind:checked={d.track} disabled={sending} />
          <span class="track-label">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
              <path d="M2.45 12C3.73 7.94 7.52 5 12 5s8.27 2.94 9.55 7c-1.28 4.06-5.07 7-9.55 7s-8.27-2.94-9.55-7Z" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            Track
          </span>
        </label>
      </div>
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 200;
    display: flex;
    flex-direction: column;
  }
  .sheet {
    background: var(--bg);
    color: var(--fg);
    font-family: var(--font-sans);
    width: 100%;
    height: 100%;
    max-width: 640px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);

    @media (min-width: 700px) {
      max-height: 92vh;
      margin-top: 4vh;
      border-radius: 16px;
      overflow: hidden;
    }
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 0.5px solid var(--border-subtle);
    gap: 8px;
  }
  .title {
    flex: 1;
    text-align: center;
    font-family: var(--font-serif);
    font-size: 18px;
    font-style: italic;
    font-weight: 400;
    color: var(--fg);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ghost, .primary {
    font: inherit;
    font-family: var(--font-sans);
    font-weight: 500;
    cursor: pointer;
    border: none;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
  }
  .ghost {
    background: transparent;
    color: var(--fg-muted);
    font-size: 14.5px;
    padding: 6px 0;
  }
  .primary {
    background: var(--accent);
    color: var(--accent-fg);
    border-radius: 999px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  .primary:disabled {
    opacity: 0.6;
    cursor: wait;
  }

  .form {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 0;
  }
  .row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 12px 20px;
    border-bottom: 0.5px solid var(--border-subtle);
  }
  .row.compact { padding: 10px 20px; }
  .lbl {
    font-family: var(--font-mono);
    color: var(--fg-subtle);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex-shrink: 0;
    min-width: 32px;
  }
  input[type="text"] {
    flex: 1;
    background: transparent;
    border: none;
    font: inherit;
    font-family: var(--font-sans);
    font-size: 15px;
    color: var(--fg);
    outline: none;
    padding: 0;
    min-width: 0;
    letter-spacing: -0.005em;
    /* Anti-zoom iOS sur focus */
    font-size: max(15px, 16px);
  }

  .link {
    background: transparent;
    border: none;
    color: var(--fg-subtle);
    cursor: pointer;
    padding: 6px 20px;
    font: inherit;
    font-family: var(--font-sans);
    font-size: 12.5px;
    text-align: left;
    align-self: flex-start;
  }

  /* === Dropdown suggestions de contacts ================================ */
  .suggestions {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    background: var(--bg-elev);
    border-bottom: 0.5px solid var(--border-subtle);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
    animation: sugg-in 140ms cubic-bezier(0.32, 0.72, 0, 1);
  }
  @keyframes sugg-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .suggestion {
    width: 100%;
    background: transparent;
    border: none;
    padding: 10px 20px;
    display: flex;
    align-items: baseline;
    gap: 8px;
    font: inherit;
    font-family: var(--font-sans);
    text-align: left;
    cursor: pointer;
    color: var(--fg);
    -webkit-tap-highlight-color: transparent;
  }
  .suggestion:active {
    background: color-mix(in srgb, var(--fg) 8%, transparent);
  }
  .sugg-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--fg);
    flex-shrink: 0;
    max-width: 50%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sugg-email {
    font-family: var(--font-mono);
    font-size: 11.5px;
    color: var(--fg-subtle);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    letter-spacing: 0.01em;
  }

  textarea {
    flex: 1;
    border: none;
    background: transparent;
    font: inherit;
    font-family: var(--font-sans);
    font-size: 15px;
    line-height: 1.55;
    padding: 14px 20px;
    color: var(--fg);
    outline: none;
    resize: none;
    min-height: 200px;
    letter-spacing: -0.005em;
    -webkit-appearance: none;
    appearance: none;
  }

  .signature {
    border-top: 0.5px dashed var(--border);
    padding: 12px 20px 16px;
    background: var(--bg-hover);
  }
  .sig-label {
    font-family: var(--font-mono);
    font-size: 9.5px;
    color: var(--fg-subtle);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    text-align: left;
    margin-bottom: 6px;
  }

  .error {
    margin: 0 16px 12px;
    padding: 10px 12px;
    background: var(--accent-soft);
    color: var(--accent);
    border-radius: 10px;
    font-size: 12.5px;
  }

  .attachments-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 20px;
    border-top: 0.5px solid var(--border-subtle);
  }
  .att-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 4px 4px 10px;
    background: var(--bg-hover);
    border: 0.5px solid var(--border-subtle);
    border-radius: 6px;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--fg-muted);
  }
  .att-name {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .att-size {
    font-family: var(--font-mono);
    color: var(--fg-subtle);
    font-size: 10px;
  }
  .att-remove {
    background: transparent;
    border: none;
    color: var(--fg-subtle);
    cursor: pointer;
    font-size: 1.1rem;
    line-height: 1;
    padding: 0 4px;
  }
  .att-remove:hover { color: var(--accent); }

  .footer-toggle {
    border-top: 0.5px solid var(--border-subtle);
    padding: 10px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background: var(--bg);
  }
  .icon-link {
    background: transparent;
    border: none;
    color: var(--fg-muted);
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    -webkit-tap-highlight-color: transparent;
  }
  .icon-link:hover { background: color-mix(in srgb, var(--fg) 6%, transparent); }
  .icon-link:disabled { opacity: 0.4; cursor: not-allowed; }
  .track-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    color: var(--fg-subtle);
    border: 0.5px solid var(--border);
    border-radius: 999px;
    padding: 6px 12px;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.1s;
  }
  .track-toggle:has(input:checked) {
    color: var(--accent);
    border-color: var(--accent);
    background: var(--accent-soft);
  }
  .track-toggle input[type="checkbox"] {
    /* Hide native checkbox, use parent state via :has() */
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
  }
  .track-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
</style>
